import type { 
  Integration, 
  IntegrationQueryParams,
  IntegrationModule,
  IntegrationModuleQueryParams,
  ModuleConfigurationSchema,
  ModuleConfiguration,
} from "../types";

import { instance } from "@/lib/axios/instance";
import type { ConfigurationContext } from "@/types/configuration";
import type { PagedResponse } from "@/types/response/PagedResponse";

// Serialize `hasModule` for the query string. Empty array → omit (no filter).
const serializeHasModule = (
  hasModule: string | string[] | null | undefined
): string | string[] | undefined => {
  if (!Array.isArray(hasModule)) return hasModule || undefined;
  return hasModule.length ? hasModule : undefined;
};

/** Response from GET /api/v2/Modules/{moduleName}/integrations. */
export type ModuleIntegrationsResponse = {
  integrations: string[];
  items: IntegrationModuleForModuleItem[];
};

/** Enriched item from Modules/{moduleName}/integrations (integrationModuleId, displayName). */
export type IntegrationModuleForModuleItem = {
  integrationModuleId: string;
  /** Present when tenant scope (invoices). Use for tenant page URLs. */
  tenantIntegrationModuleId?: string;
  /** Present when client scope. Use for campaign integration add form and routing. */
  clientIntegrationModuleId?: string;
  integrationName: string;
  displayName: string;
  openInvoiceCount?: number;
};

/** Fetches integration names that support a given module (e.g. "campaigns"). */
export const fetchIntegrationsForModule = async (
  moduleName: string,
  params: IntegrationQueryParams
): Promise<string[]> => {
  const response = await instance.get<ModuleIntegrationsResponse>(
    `/api/v2/Modules/${encodeURIComponent(moduleName)}/integrations`,
    {
      params: {
        tenantId: params.tenantId || undefined,
        clientId: params.clientId || undefined,
        isActive: true,
      },
    }
  );
  return response.data.integrations ?? [];
};

/** Fetches integration modules for a given module (e.g. "campaigns") with integrationModuleId and displayName. */
export const fetchIntegrationModulesForModule = async (
  moduleName: string,
  params: IntegrationQueryParams
): Promise<IntegrationModuleForModuleItem[]> => {
  const response = await instance.get<ModuleIntegrationsResponse>(
    `/api/v2/Modules/${encodeURIComponent(moduleName)}/integrations`,
    {
      params: {
        tenantId: params.tenantId || undefined,
        clientId: params.clientId || undefined,
        isActive: true,
      },
    }
  );
  return response.data.items ?? [];
};

/** Fetches campaign integrations that have invoice history, with open invoice counts (paginated). */
export const fetchCampaignInvoiceIntegrations = async (params: {
  tenantId?: string;
  page: number;
  pageSize: number;
  search?: string;
}): Promise<PagedResponse<IntegrationModuleForModuleItem>> => {
  const response = await instance.get<PagedResponse<IntegrationModuleForModuleItem>>(
    `/api/v2/Campaigns/Invoices/Integrations`,
    {
      params: {
        tenantId: params.tenantId || undefined,
        page: params.page,
        pageSize: params.pageSize,
        search: params.search || undefined,
      },
    }
  );
  return response.data;
};

// Fetch all integrations
export const fetchIntegrations = async (
  params: IntegrationQueryParams
): Promise<Integration[]> => {
  const response = await instance.get("/api/v2/Integrations", {
    params: {
      tenantId: params.tenantId || undefined,
      clientId: params.clientId || undefined,
      isActive: params.isActive ?? undefined,
      search: params.search || undefined,
      // Array values are serialized as repeated `hasModule=a&hasModule=b` (axios instance uses
      // indexes: null) which binds to the backend `List<string> hasModule`. Empty array → omit.
      hasModule: serializeHasModule(params.hasModule),
      orderBy: params.orderBy || undefined,
      descending: params.descending ?? undefined,
      includes: params.includes?.length ? params.includes : undefined,
    },
  });

  return response.data;
};

// Fetch integration modules by name
export const fetchIntegrationModules = async (
  params: IntegrationModuleQueryParams
): Promise<IntegrationModule[]> => {
  const response = await instance.get(
    `/api/v2/Integrations/${encodeURIComponent(params.integrationName)}/Modules`,
    {
      params: {
        tenantId: params.tenantId || undefined,
        clientId: params.clientId || undefined,
      },
    }
  );
  
  return response.data;
};

// Fetch module configuration schema
export const fetchModuleConfigurationSchema = async (
  integrationName: string,
  moduleName: string,
  context: ConfigurationContext
): Promise<ModuleConfigurationSchema> => {
  const params = context.level === 'tenant'
    ? { tenantId: context.tenantId, clientId: null }
    : { tenantId: context.tenantId, clientId: context.clientId };

  const response = await instance.get(
    `/api/v2/Integrations/${encodeURIComponent(integrationName)}/Modules/${encodeURIComponent(moduleName)}/Configuration/Schema`,
    { params }
  );
  
  return response.data;
};

// Fetch current module configuration
export const fetchModuleConfiguration = async (
  integrationName: string,
  moduleName: string,
  context: ConfigurationContext
): Promise<ModuleConfiguration> => {
  const params = context.level === 'tenant'
    ? { tenantId: context.tenantId, clientId: null }
    : { tenantId: context.tenantId, clientId: context.clientId };

  const response = await instance.get(
    `/api/v2/Integrations/${encodeURIComponent(integrationName)}/Modules/${encodeURIComponent(moduleName)}/Configuration`,
    { params }
  );
  
  return response.data.configurations || {};
};

// Atomic update: configuration + toggle in single transaction
export const updateIntegrationModuleAtomic = async (
  integrationName: string,
  moduleName: string,
  context: ConfigurationContext,
  configurations: ModuleConfiguration,
  isActive: boolean
): Promise<void> => {
  const body = context.level === 'tenant'
    ? { tenantId: context.tenantId, clientId: null, configurations, isActive }
    : { tenantId: context.tenantId, clientId: context.clientId, configurations, isActive };

  await instance.put(
    `/api/v2/Integrations/${encodeURIComponent(integrationName)}/Modules/${encodeURIComponent(moduleName)}`,
    body
  );
};

// Fetch single integration module by integration name and module name
export const fetchIntegrationModule = async (
  integrationName: string,
  moduleName: string,
  params: { tenantId?: string; clientId?: string }
): Promise<IntegrationModule | null> => {
  const response = await instance.get(
    `/api/v2/Integrations/${encodeURIComponent(integrationName)}/Modules/${encodeURIComponent(moduleName)}`,
    {
      params: {
        tenantId: params.tenantId || undefined,
        clientId: params.clientId || undefined,
      },
    }
  );
  
  return response.data;
};

// Fetch integration configuration schema
export const fetchIntegrationConfigurationSchema = async (
  integrationName: string,
  context: ConfigurationContext
): Promise<ModuleConfigurationSchema> => {
  const params = context.level === 'tenant'
    ? { tenantId: context.tenantId, clientId: null }
    : { tenantId: context.tenantId, clientId: context.clientId };

  const response = await instance.get(
    `/api/v2/Integrations/${encodeURIComponent(integrationName)}/Configuration/Schema`,
    { params }
  );
  
  return response.data;
};

// Fetch current integration configuration
export const fetchIntegrationConfiguration = async (
  integrationName: string,
  context: ConfigurationContext
): Promise<ModuleConfiguration> => {
  const params = context.level === 'tenant'
    ? { tenantId: context.tenantId, clientId: null }
    : { tenantId: context.tenantId, clientId: context.clientId };

  const response = await instance.get(
    `/api/v2/Integrations/${encodeURIComponent(integrationName)}/Configuration`,
    { params }
  );
  
  return response.data.configurations || {};
};

// Atomic update: configuration + toggle in single transaction
export const updateIntegrationConfigurationAtomic = async (
  integrationName: string,
  context: ConfigurationContext,
  configurations: ModuleConfiguration,
  isActive: boolean
): Promise<void> => {
  const body = context.level === 'tenant'
    ? { tenantId: context.tenantId, clientId: null, configurations, isActive }
    : { tenantId: context.tenantId, clientId: context.clientId, configurations, isActive };

  await instance.put(
    `/api/v2/Integrations/${encodeURIComponent(integrationName)}`,
    body
  );
};

// One integration-level API key (mirrors the backend TenantIntegrationApiKeyResponse).
export type IntegrationApiKey = {
  id: string;
  apiKey: string;
  createdDate: string;
};

// List all active integration-level API keys for an integration (newest first).
export const getIntegrationApiKeys = async (
  integrationName: string,
  tenantId: string
): Promise<IntegrationApiKey[]> => {
  const response = await instance.get(
    `/api/v2/Integrations/${encodeURIComponent(integrationName)}/ApiKey`,
    { params: { tenantId } }
  );

  return response.data;
};

// Generate a new integration-level API key.
export const generateIntegrationApiKey = async (
  integrationName: string,
  tenantId: string
): Promise<{ apiKey: string }> => {
  const response = await instance.post(
    `/api/v2/Integrations/${encodeURIComponent(integrationName)}/ApiKey`,
    { tenantId }
  );

  return response.data;
};

// Revoke a single integration-level API key by id.
export const revokeIntegrationApiKey = async (
  integrationName: string,
  apiKeyId: string,
  tenantId: string
): Promise<void> => {
  await instance.delete(
    `/api/v2/Integrations/${encodeURIComponent(integrationName)}/ApiKey/${encodeURIComponent(apiKeyId)}`,
    { params: { tenantId } }
  );
};

// Integration API client
export const integrationsApi = {
  fetchIntegrationsForModule,
  fetchIntegrationModulesForModule,
  fetchCampaignInvoiceIntegrations,
  fetchIntegrations,
  fetchIntegrationModules,
  fetchIntegrationModule,
  fetchModuleConfigurationSchema,
  fetchModuleConfiguration,
  updateIntegrationModuleAtomic,
  fetchIntegrationConfigurationSchema,
  fetchIntegrationConfiguration,
  updateIntegrationConfigurationAtomic,
  getIntegrationApiKeys,
  generateIntegrationApiKey,
  revokeIntegrationApiKey,
} as const;
